<div class="max-w-2xl mx-auto">
  <h1 class="text-2xl font-semibold text-gray-900 mb-6">
    {{ 'settings.copyContents' | translate }}
  </h1>

  @if (isLoading()) {
    <div class="flex justify-center items-center py-12">
      <cmi-loading-spinner [text]="'copyContents.loading' | translate" />
    </div>
  } @else {
    <form [formGroup]="form" (ngSubmit)="onSubmit()">
      <div class="flex flex-col gap-4">
        <div>
          <div class="flex items-center gap-2">
            <label for="sources" class="block text-sm/6 font-medium text-gray-700">
              {{ 'copyContents.sources.label' | translate }}
            </label>
            <div class="relative group">
              <svg
                class="h-4 w-4 text-gray-400 cursor-help"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div
                class="absolute left-0 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                {{ 'copyContents.sources.help' | translate }}
              </div>
            </div>
          </div>
          <div class="mt-1">
            <div
              [class]="
                'flex items-start rounded-md bg-white pl-3 outline-1 -outline-offset-1 focus-within:outline-2 focus-within:-outline-offset-2 ' +
                (form.controls.sources.invalid && form.controls.sources.touched
                  ? 'outline-red-600 focus-within:outline-red-600'
                  : 'outline-gray-300 focus-within:outline-main')
              ">
              <textarea
                id="sources"
                formControlName="sources"
                rows="4"
                class="block min-w-0 grow bg-transparent py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-500 focus:outline-none sm:text-sm/6 resize-y"></textarea>
            </div>
            @if (
              form.controls.sources.invalid &&
              form.controls.sources.touched &&
              form.controls.sources.hasError('required')
            ) {
              <p class="mt-1 text-sm text-red-600">
                {{ 'copyContents.sources.required' | translate }}
              </p>
            }
          </div>
        </div>

        <div>
          <div class="flex items-center gap-2">
            <label for="areas" class="block text-sm/6 font-medium text-gray-700">
              {{ 'copyContents.areas.label' | translate }}
            </label>
            <div class="relative group">
              <svg
                class="h-4 w-4 text-gray-400 cursor-help"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div
                class="absolute left-0 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 whitespace-pre-line">
                {{ 'copyContents.areas.help' | translate }}
              </div>
            </div>
          </div>
          <div class="mt-1">
            <div
              [class]="
                'flex items-start rounded-md bg-white pl-3 outline-1 -outline-offset-1 focus-within:outline-2 focus-within:-outline-offset-2 ' +
                (form.controls.areas.invalid && form.controls.areas.touched
                  ? 'outline-red-600 focus-within:outline-red-600'
                  : 'outline-gray-300 focus-within:outline-main')
              ">
              <textarea
                id="areas"
                formControlName="areas"
                rows="4"
                class="block min-w-0 grow bg-transparent py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-500 focus:outline-none sm:text-sm/6 resize-y"></textarea>
            </div>
            @if (
              form.controls.areas.invalid &&
              form.controls.areas.touched &&
              form.controls.areas.hasError('required')
            ) {
              <p class="mt-1 text-sm text-red-600">
                {{ 'copyContents.areas.required' | translate }}
              </p>
            }
          </div>
        </div>

        <div>
          <label for="targets" class="block text-sm/6 font-medium text-gray-700">
            {{ 'copyContents.targets.label' | translate }}
          </label>
          <div class="mt-1 flex gap-2">
            <div
              [class]="
                'flex items-center rounded-md bg-white pl-3 outline-1 -outline-offset-1 focus-within:outline-2 focus-within:-outline-offset-2 flex-1 ' +
                (form.controls.targets.invalid && form.controls.targets.touched
                  ? 'outline-red-600 focus-within:outline-red-600'
                  : 'outline-gray-300 focus-within:outline-main')
              ">
              <input
                type="number"
                id="targets"
                min="0"
                formControlName="targets"
                class="block min-w-0 grow bg-transparent py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-500 focus:outline-none sm:text-sm/6" />
            </div>
            <button
              type="button"
              (click)="onJumpToSection()"
              [disabled]="isLoading() || form.controls.targets.invalid"
              class="px-3 py-2 text-sm font-medium rounded-md bg-main text-white hover:bg-main/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              [title]="'copyContents.jumpToSection' | translate">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
              </svg>
            </button>
          </div>
          @if (
            form.controls.targets.invalid &&
            form.controls.targets.touched &&
            form.controls.targets.hasError('required')
          ) {
            <p class="mt-1 text-sm text-red-600">
              {{ 'copyContents.targets.required' | translate }}
            </p>
          }
        </div>

        <div class="flex items-center">
          <input
            type="checkbox"
            id="page-break"
            formControlName="pageBreak"
            class="h-4 w-4 rounded border-gray-300 text-main focus:ring-main" />
          <label for="page-break" class="ml-2 block text-sm text-gray-700">
            {{ 'copyContents.pageBreak' | translate }}
          </label>
        </div>

        <div>
          <h2 class="block text-sm/6 font-medium text-gray-700 mb-2">
            {{ 'copyContents.supplementData' | translate }}
          </h2>
          <div class="flex flex-col gap-2">
            <div class="flex items-center">
              <input
                type="checkbox"
                id="copy-contents-execute"
                formControlName="copyContentsExecute"
                class="h-4 w-4 rounded border-gray-300 text-main focus:ring-main" />
              <label for="copy-contents-execute" class="ml-2 block text-sm text-gray-700">
                {{ 'copyContents.copyContentsExecute' | translate }}
              </label>
            </div>
            <div class="flex items-center">
              <input
                type="checkbox"
                id="manual-page-number"
                formControlName="manualPageNumber"
                class="h-4 w-4 rounded border-gray-300 text-main focus:ring-main" />
              <label for="manual-page-number" class="ml-2 block text-sm text-gray-700">
                {{ 'copyContents.manualPageNumber' | translate }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </form>
  }
</div>

<ng-template #footerPortal>
  <div class="flex gap-4 justify-end">
    <button
      type="button"
      (click)="onDelete()"
      [disabled]="isDeleting()"
      class="px-4 py-2 h-10 text-sm font-medium rounded-lg border-2 bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:border-gray-100 transition-colors flex items-center justify-center gap-2 min-w-[120px]">
      @if (isDeleting()) {
        <svg
          class="w-4 h-4 animate-spin text-gray-700"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4" />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span>{{ 'copyContents.deleting' | translate }}</span>
      } @else {
        <span>{{ 'copyContents.delete' | translate }}</span>
      }
    </button>
    <button
      type="button"
      (click)="onSubmit()"
      [disabled]="!hasChanges() || form.invalid || saveResource.isLoading()"
      class="px-4 py-2 h-10 text-sm font-medium rounded-lg border-2 border-main bg-main text-white hover:bg-main-mid disabled:opacity-50 disabled:cursor-not-allowed disabled:border-gray-100 transition-colors flex items-center justify-center gap-2 min-w-[120px]">
      @if (saveResource.isLoading()) {
        <svg
          class="w-4 h-4 animate-spin text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4" />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span>{{ 'copyContents.saving' | translate }}</span>
      } @else {
        <span>{{ 'copyContents.apply' | translate }}</span>
      }
    </button>
  </div>
</ng-template>

<ng-template #notificationPortal>
  @if (notificationMode() && notificationMessageKey()) {
    <cmi-notification
      [mode]="notificationMode()!"
      [message]="notificationMessageKey()! | translate"
      [description]="
        notificationErrorMessage() ||
        (notificationDescriptionKey() ? (notificationDescriptionKey()! | translate) : undefined)
      " />
  }
</ng-template>
